import { z } from 'zod';

/** Whole cents only, so what's shown ("$ 0.55") is exactly what's charged. */
const dollars = (label: string, max: number, maxMessage: string) =>
  z.preprocess(
    (v) => (typeof v === 'string' ? v.trim().replace(/^\$\s*/, '') : v),
    z.coerce
      .number({ invalid_type_error: `Enter ${label}` })
      .min(0, 'Must be 0 or more')
      .max(max, maxMessage)
      .refine((n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-9, 'Use dollars and cents, e.g. 0.55'),
  );

/** Infinity EV's share: dollars to 4 decimals (e.g. 0.1000), since it can be a part of a cent. */
const share = (label: string) =>
  z.preprocess(
    (v) => (typeof v === 'string' ? v.trim().replace(/^\$\s*/, '') : v),
    z.coerce
      .number({ invalid_type_error: `Enter ${label}` })
      .min(0, 'Must be 0 or more')
      .max(5, 'That looks too high')
      .refine((n) => Math.abs(n * 10_000 - Math.round(n * 10_000)) < 1e-6, 'Use at most 4 decimal places'),
  );

/**
 * Each charger has two prices:
 *  - public: what pay-to-charge guests pay by card (card fees come out of it),
 *  - private: what drivers with the organisation's RFID cards are charged (no card fees).
 * Both include Infinity EV's share, and both can have an idle fee.
 */
export const TARIFF_KINDS = ['public', 'private'] as const;
export type TariffKind = (typeof TARIFF_KINDS)[number];
export const TARIFF_KIND_LABELS: Record<TariffKind, string> = { public: 'Public price', private: 'Private price' };

const pricePerKwh = dollars('a price in dollars, e.g. 0.55', 5, 'That looks too high: prices are in dollars per kWh');

/**
 * A new price for one or more chargers (e.g. every charger at a site). It starts now, or at
 * a set time; prices already in force are never changed, so history stays true. Each price
 * keeps Infinity EV's share of it, so what the site owner gets can be worked out.
 *
 * With a time-of-use schedule on the site, pricePerKwh is for its first period ("all other
 * times") and periodRates has one entry for each of the others, in order.
 */
export const tariffFields = z.object({
  chargerIds: z.array(z.string().uuid()).min(1, 'Choose at least one charger').max(200),
  kind: z.enum(TARIFF_KINDS).default('public'),
  /** $ per kWh. */
  pricePerKwh,
  /** The other periods of the site's schedule (peak, off-peak…), in order. */
  periodRates: z
    .array(z.object({ pricePerKwh, infinityevRatePerKwh: z.preprocess((v) => (v === '' ? undefined : v), share('a rate in dollars, e.g. 0.10').optional()) }))
    .max(3)
    .default([]),
  gstInclusive: z.boolean().default(true),
  /** $ per minute once the car has stopped charging but is still plugged in. Blank: none. */
  idleFeePerMinute: z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    dollars('a fee in dollars, e.g. 0.20', 5, 'That looks too high: idle fees are per minute').nullable(),
  ),
  /** Free minutes after charging stops before the idle fee starts. */
  idleGraceMinutes: z.preprocess(
    (v) => (v === '' || v === undefined || v === null ? 15 : v),
    z.coerce.number({ invalid_type_error: 'Enter a number of minutes' }).int('Whole minutes').min(0, '0 or more').max(240, 'At most 240 minutes'),
  ),
  /** The most idle fee one session is charged, $. Blank: an hour's worth for guests, no limit for RFID cards. */
  idleFeeMax: z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    dollars('an amount in dollars, e.g. 30.00', 1000, 'At most $1000').nullable(),
  ),
  /** ISO time it starts; leave out for straight away. */
  startsAt: z
    .string()
    .datetime({ offset: true, message: 'Enter a date and time' })
    .nullish()
    .or(z.literal(''))
    .transform((v) => (v ? v : null)),
  /** Infinity EV's share of the price, $ per kWh. Defaults to INFINITYEV_RATE_CENTS; an admin can lower it for an organisation. */
  infinityevRatePerKwh: z.preprocess((v) => (v === '' ? undefined : v), share('a rate in dollars, e.g. 0.10').optional()),
  /** Infinity EV's share of the idle fee, $ per minute. Defaults to INFINITYEV_IDLE_PERCENT of the fee. */
  infinityevIdleFeePerMinute: z.preprocess((v) => (v === '' ? undefined : v), share('a fee in dollars, e.g. 0.18').nullable().optional()),
});
export const tariffBody = tariffFields
  .refine((b) => (b.infinityevRatePerKwh ?? 0) <= b.pricePerKwh, { message: 'At most the price', path: ['infinityevRatePerKwh'] })
  .superRefine((b, ctx) =>
    b.periodRates.forEach((r, i) => {
      if ((r.infinityevRatePerKwh ?? 0) > r.pricePerKwh) ctx.addIssue({ code: 'custom', path: ['periodRates', i, 'infinityevRatePerKwh'], message: 'At most the price' });
    }),
  )
  .refine((b) => b.idleFeePerMinute === null || (b.infinityevIdleFeePerMinute ?? 0) <= b.idleFeePerMinute, { message: 'At most the idle fee', path: ['infinityevIdleFeePerMinute'] })
  .refine((b) => b.idleFeeMax === null || b.idleFeeMax > 0, { message: 'More than 0, or leave it blank', path: ['idleFeeMax'] });
export type TariffBody = z.input<typeof tariffBody>;

/** The defaults the price form starts from, from the server's settings (.env). */
export interface TariffDefaults {
  /** $ per kWh, from INFINITYEV_RATE_CENTS. */
  infinityevRatePerKwh: number;
  /** % of the idle fee, from INFINITYEV_IDLE_PERCENT. */
  infinityevIdlePercent: number;
  /** % of each payment, from SQUARE_CARD_FEE_PERCENT. */
  cardFeePercent: number;
}

/**
 * How one price splits: the card fee (a % of what the guest pays), Infinity EV's share,
 * and what's left for the site owner. Four decimals, as these are parts of a cent.
 */
export function priceSplit(price: number, infinityev: number, cardFeePercent: number) {
  const r4 = (n: number) => Math.round(n * 10_000) / 10_000;
  const cardFee = r4((price * cardFeePercent) / 100);
  return { cardFee, remittance: r4(price - infinityev - cardFee) };
}

/** Money on screen: "$ 0.55", always two decimals. */
export function formatRate(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  return `$ ${Number(value).toFixed(2)}`;
}
